import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { format } from 'date-fns'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import modalStyles from '@/features/application/styles/modals.module.css'
import {
  AdvancedDateRangePicker,
  getAngularSystemAnnouncementPresets,
} from '@/components/ui/AdvancedDateRangePicker'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import { showApiError } from '@/lib/utils/notifications'
import { cn } from '@/lib/utils/helpers'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { getRolesConfig } from '@/features/user-management/services/userManagementApi'
import {
  systemAnnouncementFormSchema,
  type SystemAnnouncementFormValues,
} from '../types/schemas'
import type {
  AnnouncementRoleColumn,
  OrgOption,
  SystemAnnouncement,
  SystemAnnouncementEditModalProps,
  SystemAnnouncementSubmitPayload,
} from '../types'
import { searchAllOrganizations } from '../services/systemAnnouncementService'
import { buildAnnouncementRoleColumns } from '../utils/buildAnnouncementRoleColumns'

function normalizeDate(value: string | undefined): string {
  if (!value) return ''
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ''
}

function coerceShowEffective(value: SystemAnnouncement['show_effective_dates']): boolean {
  if (typeof value === 'boolean') return value
  return String(value) === '1' || String(value).toLowerCase() === 'true'
}

/** Local calendar day from `YYYY-MM-DD` (avoids UTC shift from `new Date(ymd)`). */
function ymdStringToDate(ymd: string): Date | null {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** Angular `searchOrganization`: API search runs when query length is even (or empty). */
function shouldRunOrgSearch(query: string): boolean {
  return query.length % 2 === 0
}

function inputClass(hasError: boolean): string {
  const base =
    'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1597ff] focus:border-[#1597ff]'
  return hasError ? `${base} border-red-500` : `${base} border-slate-300`
}

export function SystemAnnouncementEditModal({
  isOpen,
  announcement,
  submitting,
  onClose,
  onSubmit,
}: SystemAnnouncementEditModalProps) {
  const [orgOptions, setOrgOptions] = useState<OrgOption[]>([])
  const [orgLoading, setOrgLoading] = useState(false)
  const [roleColumns, setRoleColumns] = useState<AnnouncementRoleColumn[]>([])
  const [allAnnouncementRoleKeys, setAllAnnouncementRoleKeys] = useState<string[]>([])
  const [rolesLoading, setRolesLoading] = useState(false)

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty, isValid },
  } = useForm<SystemAnnouncementFormValues>({
    resolver: zodResolver(systemAnnouncementFormSchema),
    mode: 'onChange',
    defaultValues: {
      announcement_topic: '',
      announcement_description: '',
      start_date: '',
      end_date: '',
      show_effective_dates: false,
      fk_org_id: '',
      roles: [],
    },
  })

  // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-form watch() is intentionally unmemoizable
  const selectedRoles = watch('roles')
  const startDateValue = watch('start_date')
  const endDateValue = watch('end_date')

  const announcementDatePresets = useMemo(() => getAngularSystemAnnouncementPresets(), [])

  const handleDateRangeChange = useCallback(
    (start: Date | null, end: Date | null) => {
      setValue('start_date', start ? format(start, 'yyyy-MM-dd') : '', {
        shouldValidate: true,
        shouldDirty: true,
      })
      setValue('end_date', end ? format(end, 'yyyy-MM-dd') : '', {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [setValue],
  )

  useEffect(() => {
    if (!isOpen) return
    reset({
      announcement_topic: announcement?.announcement_topic ?? '',
      announcement_description: announcement?.announcement_description ?? '',
      start_date: normalizeDate(announcement?.start_date),
      end_date: normalizeDate(announcement?.end_date),
      show_effective_dates: coerceShowEffective(announcement?.show_effective_dates),
      fk_org_id:
        announcement?.fk_org_id != null ? String(announcement.fk_org_id) : '',
      roles: announcement?.roles ? [...announcement.roles] : [],
    })
    let cancelled = false
    setOrgLoading(true)
    searchAllOrganizations()
      .then((opts) => {
        if (!cancelled) setOrgOptions(opts)
      })
      .catch((error) => {
        if (!cancelled) showApiError(error)
      })
      .finally(() => {
        if (!cancelled) setOrgLoading(false)
      })

    setRolesLoading(true)
    getRolesConfig()
      .then((roleData) => {
        if (cancelled) return
        const { columns, allRoleKeys } = buildAnnouncementRoleColumns(roleData)
        setRoleColumns(columns)
        setAllAnnouncementRoleKeys(allRoleKeys)
      })
      .catch((error) => {
        if (!cancelled) showApiError(error)
      })
      .finally(() => {
        if (!cancelled) setRolesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, announcement, reset])

  const orgSelectOptions: SearchableSelectOption[] = useMemo(
    () => orgOptions.map((o) => ({ value: String(o.value), label: o.label })),
    [orgOptions],
  )

  const internalSubmit = (values: SystemAnnouncementFormValues) => {
    const payload: SystemAnnouncementSubmitPayload = {
      announcement_topic: values.announcement_topic.trim(),
      announcement_description: values.announcement_description,
      start_date: values.start_date,
      end_date: values.end_date,
      show_effective_dates: values.show_effective_dates ? '1' : undefined,
      fk_org_id: values.fk_org_id ? Number(values.fk_org_id) : undefined,
      roles: values.roles.length ? values.roles : undefined,
    }
    onSubmit(payload, announcement?.sa_id)
  }

  const title = announcement?.sa_id ? 'Edit System Announcement' : 'Add System Announcement'

  const allRolesSelected =
    allAnnouncementRoleKeys.length > 0 &&
    selectedRoles.length === allAnnouncementRoleKeys.length &&
    allAnnouncementRoleKeys.every((k) => selectedRoles.includes(k))

  const toggleRole = (roleKey: string, checked: boolean) => {
    const next = checked
      ? selectedRoles.includes(roleKey)
        ? selectedRoles
        : [...selectedRoles, roleKey]
      : selectedRoles.filter((r) => r !== roleKey)
    setValue('roles', next, { shouldDirty: true, shouldValidate: true })
  }

  const toggleSelectAll = (checked: boolean) => {
    setValue('roles', checked ? [...allAnnouncementRoleKeys] : [], {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  return (
    <OnScreenModal isOpen={isOpen} title={title} onClose={onClose} maxWidth="max-w-3xl">
      <form onSubmit={handleSubmit(internalSubmit)} className="space-y-4 p-2">
        <div>
          <label
            htmlFor="sa-topic"
            className="block text-sm font-medium text-slate-700 mb-1"
          >
            Topic
          </label>
          <input
            id="sa-topic"
            type="text"
            className={inputClass(Boolean(errors.announcement_topic))}
            aria-invalid={Boolean(errors.announcement_topic)}
            {...register('announcement_topic')}
          />
          {errors.announcement_topic && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.announcement_topic.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
          <Controller
            name="announcement_description"
            control={control}
            render={({ field: { value, onChange } }) => (
              <CKEditor4
                id="sa-description"
                className={
                  errors.announcement_description ? 'ring-2 ring-red-500 rounded-lg' : ''
                }
                value={value || ''}
                onChange={onChange}
              />
            )}
          />
          {errors.announcement_description && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.announcement_description.message}
            </p>
          )}
        </div>

        <div
          className="space-y-1"
          role="group"
          aria-labelledby="sa-daterange-label"
          data-testid="system-announcement-date-range"
        >
          <label id="sa-daterange-label" className="block text-sm font-medium text-slate-700 mb-1">
            Announcement Start - End Date
          </label>
          <div className="relative w-full max-w-2xl">
            <AdvancedDateRangePicker
              startDate={ymdStringToDate(startDateValue || '')}
              endDate={ymdStringToDate(endDateValue || '')}
              onChange={handleDateRangeChange}
              placeholder="Select Date Range"
              customPresets={announcementDatePresets}
              monthsShown={2}
              stackMonthsVertically={false}
              displayRangeFormat="angularMmddyyyy"
              presetsSingleColumn
              dropdownPortalToBody
              triggerClassName={cn(
                'w-full min-h-[42px] rounded-lg border px-3 py-2 text-left text-sm shadow-sm hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1597ff] focus-visible:border-[#1597ff]',
                errors.start_date || errors.end_date
                  ? 'border-red-500'
                  : 'border-slate-300',
              )}
              hideTriggerIcon
            />
          </div>
          {(errors.start_date || errors.end_date) && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.start_date?.message || errors.end_date?.message}
            </p>
          )}
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" className="rounded" {...register('show_effective_dates')} />
            Show Effective Dates
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Announcement To Organization
          </label>
          <Controller
            name="fk_org_id"
            control={control}
            render={({ field: { value, onChange } }) => (
              <SearchableSelect
                options={orgSelectOptions}
                value={value}
                onChange={(next) => onChange(Array.isArray(next) ? next[0] ?? '' : next)}
                placeholder={orgLoading ? 'Loading organizations...' : 'All Organizations'}
                searchPlaceholder="Search By Organizations Name"
                loading={orgLoading}
                onSearch={(q) => {
                  if (!shouldRunOrgSearch(q)) return
                  setOrgLoading(true)
                  searchAllOrganizations(q || undefined)
                    .then(setOrgOptions)
                    .catch(showApiError)
                    .finally(() => setOrgLoading(false))
                }}
              />
            )}
          />
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium text-slate-700">Announcement To Roles</span>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="rounded border-slate-300"
                checked={allRolesSelected}
                disabled={rolesLoading || allAnnouncementRoleKeys.length === 0}
                onChange={(e) => toggleSelectAll(e.target.checked)}
              />
              Select All
            </label>
          </div>

          {rolesLoading ? (
            <ModuleScreenLoading variant="inline" className="py-6" data-testid="system-announcement-roles-loading" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border border-slate-200 rounded-lg p-3 bg-slate-50/50">
              {roleColumns.map((col) => (
                <div key={col.key}>
                  <p className="text-sm font-semibold text-slate-800 mb-2">{col.heading}</p>
                  <div className="space-y-2">
                    {col.roles.map((row) => {
                      const checked = selectedRoles.includes(row.roleKey)
                      return (
                        <div
                          key={row.roleKey}
                          className={cn(
                            'flex items-start gap-2',
                            row.displayName === 'Applicants' && 'mt-5',
                          )}
                        >
                          <input
                            id={`role_name_${row.roleKey}`}
                            type="checkbox"
                            className="mt-1 rounded border-slate-300"
                            checked={checked}
                            onChange={(e) => toggleRole(row.roleKey, e.target.checked)}
                          />
                          <label
                            htmlFor={`role_name_${row.roleKey}`}
                            className="text-sm text-slate-700 cursor-pointer leading-snug"
                          >
                            {row.displayName}
                          </label>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-center gap-3 pt-4 border-t border-slate-200 mt-6">
          <button
            type="submit"
            disabled={!isDirty || submitting || !isValid}
            className={`${primaryEntityActionButtonClass} min-w-[120px]`}
          >
            {submitting ? 'Saving...' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={`${secondaryEntityActionButtonClass} min-w-[120px]`}
          >
            Cancel
          </button>
        </div>
      </form>
    </OnScreenModal>
  )
}
